// TimeTab.jsx — the weekly timesheet, laid out like BidLight's Resource
// Tracking grid: one row per project you are working on, seven day columns of
// hours, a week total, a note, and a Total Hours footer.
//
// The one change from the original: a row is a CLIENT's project, not a bare
// project. That is the whole point of the tab — hours have to land on someone
// who can be billed for them, so the picker walks client → project and the row
// carries the client through to the roll-up.
//
// Editing follows the source too: a row is read-only until you press Edit,
// then Save writes the whole week at once. Saving per keystroke would send a
// request per digit typed, and a half-typed "12" is a very real 1.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  listClients, listTimeEntries, createTimeEntry, updateTimeEntry,
  deleteTimeEntryWeek, deleteTimeEntry, copyPreviousWeek,
} from "../../api/invoices/invoices";
import {
  MAX_HOURS_PER_DAY, addWeeks, columnLabel, dayTotals, hoursFor, isoDay,
  noteFor, overCapDays, startOfWeek, weekDates, weekLabel, weekTotal,
} from "./weeks";

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: 520, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', borderRadius: 8, padding: 22 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-3">
          <h5 className="mb-0">{title}</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────── Add a project to the sheet
function AddRowModal({ clients, onClose, onAdded }) {
  const [clientId, setClientId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [direct, setDirect] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const client = useMemo(() => clients.find((c) => String(c._id) === clientId), [clients, clientId]);
  // Archived projects stay visible in the Clients tab as history, but you
  // cannot start logging new time against one.
  const projects = useMemo(
    () => ((client && client.projects) || []).filter((p) => p.status !== 'archived'),
    [client],
  );

  const submit = async () => {
    setError("");
    if (!projectId) { setError("Pick a project"); return; }
    setBusy(true);
    try {
      const r = await createTimeEntry({ projectId, direct });
      onAdded(r && r.data, r && r.existed);
    } catch (e) { setError((e && e.message) || "Could not add the row"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Add project / time" onClose={onClose}>
      {error && <div className="alert alert-warning" style={{ fontSize: 13 }}>{error}</div>}
      <label style={{ fontSize: 12, color: '#666' }}>Client</label>
      <select className="form-control mb-2" value={clientId}
        onChange={(e) => { setClientId(e.target.value); setProjectId(""); }}>
        <option value="">Select a client…</option>
        {clients.map((c) => (<option key={c._id} value={c._id}>{c.name}</option>))}
      </select>

      <label style={{ fontSize: 12, color: '#666' }}>Project</label>
      <select className="form-control mb-2" value={projectId} disabled={!clientId}
        onChange={(e) => setProjectId(e.target.value)}>
        <option value="">{clientId ? "Select a project…" : "Pick a client first"}</option>
        {projects.map((p) => (<option key={p._id} value={p._id}>{p.name}{p.code ? ` (${p.code})` : ''}</option>))}
      </select>
      {clientId && projects.length === 0 && (
        <div style={{ fontSize: 12, color: '#a15c00', marginBottom: 8 }}>
          This client has no active projects. Add one in the Clients tab first.
        </div>
      )}

      <label style={{ fontSize: 12, color: '#666' }}>Type</label>
      <select className="form-control mb-2" value={direct ? 'direct' : 'indirect'}
        onChange={(e) => setDirect(e.target.value === 'direct')}>
        <option value="direct">Direct — billable client work</option>
        <option value="indirect">Indirect — internal time on this project</option>
      </select>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="acc-btn acc-btn-primary" onClick={submit} disabled={busy || !projectId}>
          {busy ? "Adding…" : "Add to my week"}
        </button>
      </div>
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────── The grid
export default function TimeTab({ can }) {
  const [anchor, setAnchor] = useState(() => startOfWeek(new Date()));
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [adding, setAdding] = useState(false);
  // entryId → { hours: { isoDay: value }, note } while a row is being edited.
  const [drafts, setDrafts] = useState({});

  const days = useMemo(() => weekDates(anchor), [anchor]);

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const r = await listTimeEntries({ week: isoDay(anchor) });
      setEntries((r && r.data) || []);
    } catch (e) { setErr((e && e.message) || "Failed to load the week"); setEntries([]); }
    finally { setBusy(false); }
  }, [anchor]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => {
      try { const r = await listClients(); setClients((r && r.data) || []); }
      catch (_) { /* the picker just shows empty */ }
    })();
  }, []);

  // Moving weeks abandons half-typed edits rather than carrying them onto
  // another week's cells, which is the one way this grid could write hours
  // onto days nobody was looking at.
  const goWeek = (n) => { setDrafts({}); setAnchor(addWeeks(anchor, n)); };

  const startEdit = (e) => setDrafts((p) => ({ ...p, [e._id]: { hours: {}, note: noteFor(e, anchor) } }));
  const cancelEdit = (id) => setDrafts((p) => { const n = { ...p }; delete n[id]; return n; });
  const setCell = (id, date, value) => setDrafts((p) => ({
    ...p, [id]: { ...p[id], hours: { ...((p[id] || {}).hours || {}), [isoDay(date)]: value } },
  }));
  const setNote = (id, value) => setDrafts((p) => ({ ...p, [id]: { ...p[id], note: value } }));

  const overCap = useMemo(() => overCapDays(entries, drafts, anchor), [entries, drafts, anchor]);

  const save = async (entry) => {
    const draft = drafts[entry._id];
    if (!draft) return;
    setErr("");
    // Only the cells actually touched are sent — an untouched day must keep
    // whatever is stored rather than being re-asserted as the value we drew.
    const dates = Object.entries(draft.hours || {}).map(([iso, v]) => ({ date: iso, hours: Number(v) || 0 }));
    const body = { week: isoDay(anchor), dates };
    if (draft.note !== noteFor(entry, anchor)) {
      // The note belongs to the week, so it is written on the Monday.
      body.notes = [{ date: isoDay(days[0]), note: draft.note || '' }];
    }
    if (!dates.length && !body.notes) { cancelEdit(entry._id); return; }
    setBusy(true);
    try {
      await updateTimeEntry(entry._id, body);
      cancelEdit(entry._id);
      await load();
    } catch (e) { setErr((e && e.message) || "Could not save the row"); }
    finally { setBusy(false); }
  };

  const clearWeek = async (entry) => {
    if (!window.confirm(`Clear this week's hours for ${entry.project_id?.name || 'this project'}? Other weeks are untouched.`)) return;
    setBusy(true);
    try { await deleteTimeEntryWeek(entry._id, isoDay(anchor)); await load(); }
    catch (e) { setErr((e && e.message) || "Could not clear the week"); }
    finally { setBusy(false); }
  };

  const removeRow = async (entry) => {
    if (!window.confirm(`Remove ${entry.project_id?.name || 'this project'} from your timesheet? Hours already logged in other weeks stay recorded.`)) return;
    setBusy(true);
    try { await deleteTimeEntry(entry._id); await load(); }
    catch (e) { setErr((e && e.message) || "Could not remove the row"); }
    finally { setBusy(false); }
  };

  const copyPrevious = async () => {
    setBusy(true); setErr("");
    try {
      const r = await copyPreviousWeek(isoDay(anchor));
      await load();
      if (r && r.data && !r.data.copied) setErr("Nothing to copy — last week has no hours on these projects.");
    } catch (e) { setErr((e && e.message) || "Could not copy last week"); }
    finally { setBusy(false); }
  };

  const totals = useMemo(() => dayTotals(entries, anchor), [entries, anchor]);
  const grandTotal = totals.reduce((s, t) => s + t.hours, 0);

  return (
    <div>
      <style>{`
        .ts-toolbar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
        .ts-week { font-weight: 600; font-size: 13px; min-width: 190px; text-align: center; }
        .ts-table { width: 100%; font-size: 13px; }
        .ts-table th { font-size: 11px; color: #888; text-transform: uppercase; text-align: left; font-weight: 500; border-bottom: 1px solid #f1f1f1; padding: 6px 8px; white-space: nowrap; }
        .ts-table td { padding: 4px 8px; border-bottom: 1px solid #f4f4f4; vertical-align: middle; }
        .ts-hours { width: 56px; text-align: center; }
        .ts-total { font-weight: 600; }
        .ts-foot td { border-top: 2px solid #eee; font-weight: 600; }
        .ts-overcap { background: #fff3f3; }
      `}</style>

      <div className="ts-toolbar">
        <button className="acc-btn" onClick={() => goWeek(-1)} disabled={busy}>← Previous</button>
        <span className="ts-week">{weekLabel(anchor)}</span>
        <button className="acc-btn" onClick={() => goWeek(1)} disabled={busy}>Next →</button>
        <button className="acc-btn" onClick={() => { setDrafts({}); setAnchor(startOfWeek(new Date())); }} disabled={busy}>This week</button>
        <span style={{ flex: 1 }} />
        {can('TIME_ENTRY_EDIT') && (
          <button className="acc-btn" onClick={copyPrevious} disabled={busy || entries.length === 0}>Copy previous</button>
        )}
        {can('TIME_ENTRY_ADD') && (
          <button className="acc-btn acc-btn-primary" onClick={() => setAdding(true)} disabled={busy}>+ Add project / time</button>
        )}
      </div>

      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
      {overCap.length > 0 && (
        <div className="alert alert-warning" style={{ fontSize: 13 }}>
          {overCap.map((d) => `${columnLabel(d.date)} is at ${d.hours}h`).join(', ')} — a day holds {MAX_HOURS_PER_DAY}.
        </div>
      )}

      {busy && entries.length === 0 && <div className="cm-empty">Loading…</div>}

      {/* The week ALWAYS renders, empty or not.
          This used to be gated on `entries.length > 0`, so a timesheet with no
          rows yet showed a sentence and nothing else — no day columns, no
          week. That reads as a broken page rather than an empty one, and it is
          not how the grid this was modelled on behaves: it draws the week
          first and fills rows into it. The empty state now sits INSIDE the
          table, under the seven days it is telling you about. */}
      {!busy && (
        <div style={{ overflowX: 'auto' }}>
          <table className="ts-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Project</th>
                <th>Type</th>
                {days.map((d) => (<th key={isoDay(d)} style={{ textAlign: 'center' }}>{columnLabel(d)}</th>))}
                <th style={{ textAlign: 'center' }}>Week total</th>
                <th>Notes</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && (
                <tr>
                  <td colSpan={days.length + 6} className="cm-empty" style={{ textAlign: 'center' }}>
                    No projects on your timesheet for this week. Use <strong>+ Add project / time</strong> to put one here.
                  </td>
                </tr>
              )}
              {entries.map((e) => {
                const draft = drafts[e._id];
                const editing = !!draft;
                return (
                  <tr key={e._id}>
                    <td>{e.client_id?.name || '—'}</td>
                    <td>{e.project_id?.name || '—'}{e.project_id?.code ? ` (${e.project_id.code})` : ''}</td>
                    <td>{e.direct ? 'Direct' : 'Indirect'}</td>
                    {days.map((d) => {
                      const iso = isoDay(d);
                      const value = editing && draft.hours[iso] != null ? draft.hours[iso] : hoursFor(e, d);
                      const flagged = overCap.some((x) => isoDay(x.date) === iso);
                      return (
                        <td key={iso} className={flagged ? 'ts-overcap' : ''} style={{ textAlign: 'center' }}>
                          <input
                            type="number" min="0" max={MAX_HOURS_PER_DAY} step="0.25"
                            className="form-control ts-hours"
                            disabled={!editing}
                            value={value}
                            onChange={(ev) => setCell(e._id, d, ev.target.value)}
                          />
                        </td>
                      );
                    })}
                    <td className="ts-total" style={{ textAlign: 'center' }}>
                      {editing
                        ? days.reduce((s, d) => s + (draft.hours[isoDay(d)] != null ? Number(draft.hours[isoDay(d)]) || 0 : hoursFor(e, d)), 0)
                        : weekTotal(e, anchor)}
                    </td>
                    <td>
                      <input
                        type="text" className="form-control" style={{ minWidth: 140 }}
                        disabled={!editing}
                        value={editing ? (draft.note || '') : noteFor(e, anchor)}
                        onChange={(ev) => setNote(e._id, ev.target.value)}
                      />
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {!editing && can('TIME_ENTRY_EDIT') && (
                        <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => startEdit(e)}>Edit</button>
                      )}
                      {editing && (
                        <>
                          <button className="acc-btn acc-btn-primary" style={{ fontSize: 11, padding: '2px 8px' }}
                            onClick={() => save(e)} disabled={busy || overCap.length > 0}>Save</button>
                          <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px', marginLeft: 4 }}
                            onClick={() => cancelEdit(e._id)}>Cancel</button>
                        </>
                      )}
                      {can('TIME_ENTRY_DELETE') && !editing && (
                        <>
                          <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px', marginLeft: 4 }}
                            onClick={() => clearWeek(e)} title="Clear only this week">Clear week</button>
                          <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px', marginLeft: 4 }}
                            onClick={() => removeRow(e)} title="Remove this project from the sheet">Remove</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="ts-foot">
                <td colSpan={3}>Total hours</td>
                {totals.map((t) => (<td key={isoDay(t.date)} style={{ textAlign: 'center' }}>{t.hours}</td>))}
                <td style={{ textAlign: 'center' }}>{grandTotal}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {adding && (
        <AddRowModal
          clients={clients}
          onClose={() => setAdding(false)}
          onAdded={(_row, existed) => {
            setAdding(false);
            if (existed) setErr("That project is already on your timesheet for this type.");
            load();
          }}
        />
      )}
    </div>
  );
}
